import React from 'react';
import styled, { css } from 'styled-components';

import { ManualCSSTheme, useCurrentTheme } from 'app/shared/theme';
import { InputProps } from 'app/shared/typings/input';
import { withShadow } from 'app/shared/components/styleUtils/manualCSS/withShadow';
import { withTextfieldStyle } from 'app/shared/components/styleUtils/manualCSS/withTextfieldStyle';
import { Icon } from 'app/shared/components/atoms/IconManualCSS';
import { Spinner } from 'app/shared/components/atoms/SpinnerManualCSS';

import { SelectState } from './index';

interface SelectInputProps {
  isOpen?: boolean;
  readonly?: boolean;
  disabled?: boolean;
  invertColor?: boolean;
}

interface Wrapper {
  children?: React.ReactNode;
  isOpen?: boolean;
  hasError?: boolean;
  invertColor?: boolean;
  readOnly?: boolean;
  disabled?: boolean;
  initialWidth?: string;
  theme: ManualCSSTheme;
}

export interface ActionButtonColors {
  primary: string;
  hover: string;
  active: string;
}

interface Props {
  invertColor?: boolean;
  actionButtonColors?: ActionButtonColors;
  isOpen?: boolean;
  onToggle: () => void;
  innerRef?: React.RefObject<HTMLInputElement>;
  name?: string;
  hasError?: boolean;
  renderLeftIcon?: React.ReactNode;
  state: SelectState;
  initialWidth?: string;
  'data-qaid'?: string;
  className?: string;

  inputProps: {
    id?: string;
    placeholder?: string;
    readOnly?: boolean;
    disabled?: boolean;
    name?: string;
    value?: string;
    onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onClear?: () => void;
  };
}

interface ButtonProps {
  isOpen?: boolean;
  children?: React.ReactNode;
  invertColor?: boolean;
  marginRight?: string;
  actionButtonColors?: ActionButtonColors;
}

const Wrapper = styled.div<Wrapper>`
  display: flex;
  flex: center;
  align-items: center;
  ${withTextfieldStyle};
  ${({ theme, readOnly, disabled, invertColor, isOpen, initialWidth }) => css`
    ${!theme.isFanFacing && `padding-right: ${theme.ruler[2]}px;`}
    border-color: ${theme.colors.darkBorder};

    ${
      readOnly &&
      css`
        cursor: pointer;
      `
    }

    ${
      disabled &&
      css`
      opacity: 0.5;
      cursor: not-allowed;
      pointer-events: none;
    `
    }

    ${
      isOpen &&
      css`
        ${withShadow({ depth: 3 })}

        &:hover {
          border-color: ${
            theme.isFanFacing ? theme.colors.backToBlack : theme.colors.macyGrey
          };
        }
      `
    }

    ${
      invertColor &&
      css`
        background: transparent;
        border-color: ${theme.colors.whiteDenim};

        &:hover {
          border-color: ${theme.colors.whiteDenim};
        }
        &:focus {
          border-color: ${theme.colors.silverSprings};
          background: rgba(219, 219, 219, 0.1); //macyGrey at 10%
        }
      `
    }

    ${
      initialWidth &&
      css`
        width: 100%;
        max-width: ${initialWidth};
      `
    }
  `}
`;

const IconWrapper = styled.div<{ invertColor?: boolean }>`
  ${({ theme, invertColor }) => css`
    margin-right: ${theme.ruler[2]}px;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;

    i::before {
      color: ${theme.colors[invertColor ? 'whiteDenim' : 'darkBorder']};
    }
  `}
`;

const StyledInput = styled.input<InputProps & SelectInputProps>`
  ${({ theme, invertColor, readOnly }) => css`
    background: transparent;
    height: 100%;
    width: 100%;
    border: none;
    font-size: ${theme.fontSizes.body2.xs};
    font-weight: 400;
    letter-spacing: 0.1px;
    color: ${theme.colors.backToBlack};

    ${
      readOnly &&
      css`
        cursor: pointer;
      `
    }

    &:hover, &:focus {
      outline: none;
    }

    &::placeholder {
      color: ${theme.colors.darkBorder};
    }

    ${
      invertColor &&
      css`
        color: ${theme.colors.whiteDenim};

        &::placeholder {
          color: ${theme.colors.whiteDenim};
        }
      `
    }
  `}
`;

const ActionButton = styled.button<ButtonProps>`
  ${({ theme, invertColor, marginRight, isOpen, actionButtonColors }) => css`
    width: ${theme.isFanFacing ? '20px' : '25px'};
    height: ${theme.isFanFacing ? '20px' : '25px'};
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.15s ease;
    cursor: pointer;
    border-radius: 100%;
    ${marginRight && `margin-right: ${marginRight};`}
    padding: ${theme.isFanFacing ? '6px;' : '0 6px 2px 6px;'}
    ${theme.isFanFacing && `background-color: ${theme.colors.primary};`}
    ${actionButtonColors && `background-color: ${actionButtonColors.primary};`}

    ${
      isOpen &&
      theme.isFanFacing &&
      `background-color: ${
        invertColor ? theme.colors.paintItBlack : theme.colors.primaryActive
      };`
    }

    ${
      isOpen &&
      actionButtonColors &&
      `background-color: ${actionButtonColors.primary};`
    }

    &:hover {
      background-color: ${
        theme.colors[
          invertColor
            ? 'paintItBlack'
            : theme.isFanFacing
              ? 'primaryHover'
              : 'silverSprings'
        ]
      };
      ${actionButtonColors && `background-color: ${actionButtonColors.hover};`}

      ${
        isOpen &&
        theme.isFanFacing &&
        `background-color: ${
          invertColor ? theme.colors.paintItBlack : theme.colors.primaryActive
        };`
      }

      ${
        isOpen &&
        actionButtonColors &&
        `background-color: ${actionButtonColors.primary};`
      }
    }

    &:focus {
      outline: none;
      ${
        theme.isFanFacing &&
        `background-color: ${
          isOpen ? theme.colors.primaryActive : theme.colors.primaryHover
        };`
      }

      ${actionButtonColors && `background-color: ${actionButtonColors.hover};`}
    }

    &:active {
      ${theme.isFanFacing && `background-color: ${theme.colors.primaryActive};`}
      ${actionButtonColors && `background-color: ${actionButtonColors.active};`}
    }

    i::before {
      color: ${theme.colors[invertColor ? 'whiteDenim' : 'darkBorder']};
    }
  `}
`;

const Input: React.FC<React.PropsWithChildren<Props>> = ({
  isOpen,
  onToggle,
  inputProps = {},
  name,
  innerRef,
  hasError,
  invertColor,
  actionButtonColors,
  renderLeftIcon,
  state,
  initialWidth,
  className,
  'data-qaid': qaId,
}) => {
  const { isFanFacing } = useCurrentTheme();

  const showClearButton =
    state === SelectState.ready &&
    !inputProps.readOnly &&
    inputProps.onClear &&
    inputProps.value;

  return (
    <Wrapper
      id={inputProps.id}
      readOnly={inputProps.readOnly}
      disabled={inputProps.disabled}
      tabIndex={0}
      hasError={hasError}
      onClick={onToggle}
      isOpen={isOpen}
      ref={innerRef}
      invertColor={invertColor}
      initialWidth={initialWidth}
      data-qaid={qaId}
      className={className}
    >
      {renderLeftIcon && (
        <IconWrapper invertColor={invertColor} data-qaid={`${qaId}-left-icon`}>
          {renderLeftIcon}
        </IconWrapper>
      )}
      <StyledInput
        value={inputProps.value}
        readOnly={inputProps.readOnly}
        disabled={inputProps.disabled}
        isOpen={isOpen}
        placeholder={inputProps.placeholder || 'Please Select'}
        name={inputProps.name}
        onChange={inputProps.onChange}
        invertColor={invertColor}
        data-qaid={`${qaId}-input`}
        autoComplete="nope "
        spellCheck="false"
      />
      {/* This hides the autocomplete on selects */}
      <input
        type="text"
        autoComplete="on"
        value=""
        style={{
          display: 'none',
          opacity: 0,
          position: 'absolute',
          left: '-100000px',
        }}
        readOnly={true}
      />

      {state === SelectState.loading && (
        <div style={{ width: '30px' }}>
          <Spinner data-qaid={`${qaId}-spinner`} size="20px" />
        </div>
      )}

      {state === SelectState.error && (
        <Icon
          data-qaid={`${qaId}-error-icon`}
          color="redRedWine"
          name="alertTriangle"
          title="Could not load options..."
        />
      )}

      {showClearButton && (
        <ActionButton
          invertColor={invertColor}
          type="button"
          data-qaid={`${qaId}-clear`}
          onClick={inputProps.onClear}
          title="Clear"
          marginRight="10px"
        >
          <Icon
            data-qaid={`${qaId}-clear-icon`}
            name="close"
            size={isFanFacing ? '8px' : '13px'}
          />
        </ActionButton>
      )}

      <ActionButton
        invertColor={invertColor}
        actionButtonColors={actionButtonColors}
        isOpen={isOpen}
        type="button"
        data-qaid={`${qaId}-toggle`}
        title="Toggle"
        name={name}
      >
        <Icon
          data-qaid={`${qaId}-toggle-icon`}
          name={isOpen ? 'caretUp' : 'caretDown'}
          size={isFanFacing ? '8px' : '13px'}
        />
      </ActionButton>
    </Wrapper>
  );
};
export default Input;
